import { getDemoBrowserId } from "../utils/demoFlow";

async function buildAuthHeaders(getToken) {
  const headers = {};
  const demoSessionId = getDemoBrowserId();
  if (demoSessionId) headers["x-browser-session-id"] = demoSessionId;
  if (typeof getToken !== "function") return headers;
  try {
    const token = await getToken();
    if (!token) return headers;
    return { ...headers, Authorization: `Bearer ${token}` };
  } catch {
    return headers;
  }
}

async function fetchJson(url, options = {}) {
  const timeoutMs = typeof options?.timeoutMs === "number" ? options.timeoutMs : 12000;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort("request_timeout"), timeoutMs);
  let res;
  try {
    res = await fetch(url, {
      credentials: "include",
      signal: controller.signal,
      ...options,
    });
  } catch (err) {
    if (err?.name === "AbortError") {
      const timeoutError = new Error(`Request timed out after ${timeoutMs}ms: ${url}`);
      timeoutError.code = "request_timeout";
      timeoutError.status = 408;
      throw timeoutError;
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
  if (res.status === 304) {
    const notModified = new Error("Not Modified");
    notModified.status = 304;
    throw notModified;
  }
  const text = await res.text();
  let data = null;
  let parseOk = false;
  try {
    data = text ? JSON.parse(text) : null;
    parseOk = true;
  } catch (e) {
    data = null;
  }
  if (res.ok && text && !parseOk) {
    const error = new Error(`Invalid JSON response from ${url}`);
    error.status = res.status;
    error.code = "invalid_json_response";
    error.data = {
      preview: String(text).slice(0, 400),
      contentType: res.headers.get("content-type") || "",
    };
    throw error;
  }
  // Debug hooks removed.
  if (!res.ok) {
    const error = new Error(data?.error || res.statusText);
    error.status = res.status;
    error.data = data;
    error.code = data?.code || "";
    throw error;
  }
  return data;
}

function toTimestamp(value) {
  const n = Number(value);
  if (Number.isFinite(n) && n > 0) return n;
  if (typeof value === "string" && value.trim()) {
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return 0;
}

function initiativeUpdatedAt(initiative) {
  if (!initiative || typeof initiative !== "object") return 0;
  return Math.max(
    toTimestamp(initiative?.updatedAt),
    toTimestamp(initiative?.meta?.updatedAt),
    0
  );
}

function mergeInitiatives(remoteInitiatives, localInitiatives) {
  const remoteAllIds = Array.isArray(remoteInitiatives?.allIds) ? remoteInitiatives.allIds : [];
  const localAllIds = Array.isArray(localInitiatives?.allIds) ? localInitiatives.allIds : [];
  const remoteById = remoteInitiatives?.byId && typeof remoteInitiatives.byId === "object" ? remoteInitiatives.byId : {};
  const localById = localInitiatives?.byId && typeof localInitiatives.byId === "object" ? localInitiatives.byId : {};

  const allIds = Array.from(new Set([...remoteAllIds, ...localAllIds]));
  const byId = {};

  allIds.forEach((id) => {
    const remoteInit = remoteById[id];
    const localInit = localById[id];
    if (!remoteInit && localInit) {
      byId[id] = localInit;
      return;
    }
    if (remoteInit && !localInit) {
      byId[id] = remoteInit;
      return;
    }
    if (!remoteInit && !localInit) return;

    const remoteTs = initiativeUpdatedAt(remoteInit);
    const localTs = initiativeUpdatedAt(localInit);
    byId[id] = localTs >= remoteTs ? localInit : remoteInit;
  });

  return { allIds, byId };
}

function mergeProjectSnapshots(remoteSnapshot, localSnapshot) {
  if (!remoteSnapshot || typeof remoteSnapshot !== "object") return localSnapshot;
  if (!localSnapshot || typeof localSnapshot !== "object") return remoteSnapshot;

  const merged = {
    ...remoteSnapshot,
    ...localSnapshot,
  };

  merged.meta = {
    ...(remoteSnapshot.meta || {}),
    ...(localSnapshot.meta || {}),
  };

  const remoteProjectTs = Math.max(
    toTimestamp(remoteSnapshot?.updatedAt),
    toTimestamp(remoteSnapshot?.meta?.updatedAt),
    0
  );
  const localProjectTs = Math.max(
    toTimestamp(localSnapshot?.updatedAt),
    toTimestamp(localSnapshot?.meta?.updatedAt),
    0
  );
  const effectiveProjectTs = Math.max(remoteProjectTs, localProjectTs);
  if (effectiveProjectTs) {
    merged.updatedAt = effectiveProjectTs;
    merged.meta.updatedAt = effectiveProjectTs;
  }

  merged.initiatives = mergeInitiatives(
    remoteSnapshot?.initiatives,
    localSnapshot?.initiatives
  );

  return merged;
}

export function createApiStorageAdapter({ getToken }) {
  async function putProject(projectId, snapshot, version) {
    const authHeaders = await buildAuthHeaders(getToken);
    return fetchJson(`/api/projects/${projectId}`, {
      method: "PUT",
      timeoutMs: 30000,
      headers: { "Content-Type": "application/json", ...authHeaders },
      body: JSON.stringify({
        data: snapshot,
        version,
      }),
    });
  }

  return {
    async loadProjects({ etag } = {}) {
      const authHeaders = await buildAuthHeaders(getToken);
      const headers = { "Content-Type": "application/json", ...authHeaders };
      if (etag) headers["If-None-Match"] = etag;
      const data = await fetchJson("/api/projects", {
        timeoutMs: 20000,
        headers,
      });
      const list = Array.isArray(data?.projects) ? data.projects : [];
      return { projects: list, etag: data?.etag || null };
    },
    async loadProject(projectId) {
      const authHeaders = await buildAuthHeaders(getToken);
      const raw = await fetchJson(`/api/projects/${projectId}`, {
        timeoutMs: 8000,
        headers: { "Content-Type": "application/json", ...authHeaders },
      });
      const payload = raw?.data ?? raw?.project ?? raw;
      if (!payload || typeof payload !== "object") {
        return { data: null, version: raw?.version || 1 };
      }
      const originalId = payload.id;
      const normalizedProject = {
        ...payload,
        id: projectId,
        meta: {
          ...(payload.meta || {}),
          ...(originalId ? { cloudId: originalId } : {}),
        },
      };
      return { data: normalizedProject, version: raw?.version || 1 };
    },
    async createProject(name) {
      const authHeaders = await buildAuthHeaders(getToken);
      const data = await fetchJson("/api/projects", {
        method: "POST",
        timeoutMs: 8000,
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({ name }),
      });
      const id =
        (typeof data?.id === "string" && data.id.trim()) ||
        (typeof data?.projectId === "string" && data.projectId.trim()) ||
        (typeof data?.project?.id === "string" && data.project.id.trim()) ||
        null;
      if (!id) {
        const error = new Error("Create project API returned no project id.");
        error.code = "project_create_missing_id";
        error.status = 500;
        error.data = data;
        throw error;
      }
      return { id };
    },
    async saveProject(projectId, snapshot, options = {}) {
      const maxConflictRetries = 3;
      let version = options?.version;
      let candidateSnapshot = snapshot;
      let data;

      for (let attempt = 0; attempt <= maxConflictRetries; attempt += 1) {
        try {
          data = await putProject(projectId, candidateSnapshot, version);
          break;
        } catch (err) {
          const status = Number(err?.status || 0);
          if (status !== 409) throw err;

          const serverVersion = Number(err?.data?.currentVersion);
          if (attempt >= maxConflictRetries) throw err;

          const latest = await this.loadProject(projectId);
          candidateSnapshot = mergeProjectSnapshots(latest?.data, candidateSnapshot);
          version = Number(
            latest?.version || serverVersion || version || 1
          );
        }
      }

      return { version: data?.version || options?.version || 1 };
    },
    async deleteProject(projectId) {
      const authHeaders = await buildAuthHeaders(getToken);
      try {
        await fetchJson(`/api/projects/${projectId}`, {
          method: "DELETE",
          timeoutMs: 8000,
          headers: { "Content-Type": "application/json", ...authHeaders },
        });
      } catch (err) {
        if (Number(err?.status || 0) !== 404) {
          throw err;
        }
      }
      return { ok: true };
    },
  };
}
